import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { Schedule } from '@/api/client/models/index.ts';
import { useApiClient } from '@/hooks/api-hook.ts';
import { dateFormatter } from '@/stores/definitions.js';

type ScheduleRow = Omit<Schedule, 'departureDate' | 'originalDepartureDateTime' | 'departureDateTime' | 'lastScheduleChangeDateTime'> & {
  departureDate?: string | null;
  originalDepartureDateTime?: string | null;
  departureDateTime?: string | null;
  lastScheduleChangeDateTime?: string | null;
};

function formatDateTime(value: Date | null | undefined): string | null {
  if (!value) {
    return null;
  }

  const formattedValue = dateFormatter(value);
  return `${formattedValue.date} ${formattedValue.timeWithoutSeconds}`;
}

function normalizeScheduleRows(rows: Schedule[] | undefined): ScheduleRow[] {
  return [...(rows ?? [])]
    .map(entry => ({
      ...entry,
      departureDate: entry.departureDate?.toString() ?? null,
      originalDepartureDateTime: formatDateTime(entry.originalDepartureDateTime),
      departureDateTime: formatDateTime(entry.departureDateTime),
      lastScheduleChangeDateTime: formatDateTime(entry.lastScheduleChangeDateTime),
    }))
    .sort((left, right) => Number(left.unseen ?? 0) - Number(right.unseen ?? 0));
}

export const useScheduleStore = defineStore('schedule', () => {
  const apiClient = useApiClient();

  const list = ref<ScheduleRow[]>([]);
  const loading = ref<boolean>(false);

  async function get(): Promise<ScheduleRow[]> {
    loading.value = true;

    try {
      const response = await apiClient.schedule.get();
      list.value = normalizeScheduleRows(response);
      return list.value;
    } finally {
      loading.value = false;
    }
  }

  return {
    list,
    loading,
    get,
  };
});
